import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { assetUrl } from "@/lib/api";
import { MegaPanel } from "@/components/site/MegaMenu";
import { MobileMenu } from "@/components/site/MobileMenu";
import { SmartLink } from "@/components/site/SmartLink";
import { cn } from "@/lib/utils";

const itemCls = "relative h-20 flex items-center gap-1 px-3 text-[15px] font-semibold text-charcoal hover:text-brand transition-colors duration-200";

const DropdownItem = ({ it, open, setOpen }) => (
  <div className="relative" onMouseEnter={() => setOpen(it.id)} onMouseLeave={() => setOpen(null)}>
    <button type="button" className={itemCls} data-testid={`nav-${it.id}`}>
      {it.label} <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", open === it.id && "rotate-180")} />
    </button>
    <AnimatePresence>
      {open === it.id && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
          className="absolute right-0 top-full min-w-[240px] bg-white border-t-[3px] border-brand rounded-b-xl shadow-xl py-2" data-testid={`nav-dropdown-${it.id}`}>
          {it.children.filter((c) => !c.hidden).map((c) => (
            <SmartLink key={c.id} to={c.to} onClick={() => setOpen(null)} className="block px-5 py-2.5 text-sm text-charcoal hover:bg-ivory hover:text-brand transition-colors" data-testid={`nav-${c.id}`}>{c.label}</SmartLink>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Header = () => {
  const { content } = useSite();
  const [open, setOpen] = useState(null);
  const { pathname } = useLocation();
  const items = content.menu.items.filter((i) => !i.hidden);

  useEffect(() => {
    setOpen(null);
  }, [pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-[0_1px_0_rgba(31,28,27,0.08)]" data-testid="site-header">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" className="shrink-0" data-testid="header-logo-link">
          <img src={assetUrl(content.general.logo)} alt={content.general.site_name} className="h-9 sm:h-11 lg:h-12 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center" data-testid="desktop-nav">
          {items.map((it) => {
            if (it.type === "mega") {
              return (
                <div key={it.id} onMouseEnter={() => setOpen(it.id)} onMouseLeave={() => setOpen(null)}>
                  <button type="button" className={itemCls} data-testid={`nav-${it.id}`}>
                    {it.label} <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", open === it.id && "rotate-180")} />
                  </button>
                  <AnimatePresence>{open === it.id && <MegaPanel onClose={() => setOpen(null)} />}</AnimatePresence>
                </div>
              );
            }
            if (it.type === "dropdown") return <DropdownItem key={it.id} it={it} open={open} setOpen={setOpen} />;
            if (/^(https?:|mailto:)/.test(it.to)) return <SmartLink key={it.id} to={it.to} className={itemCls} data-testid={`nav-${it.id}`}>{it.label}</SmartLink>;
            return (
              <NavLink key={it.id} to={it.to || "/"} end className={({ isActive }) => cn(itemCls, isActive && "text-brand")} data-testid={`nav-${it.id}`}>
                {it.label}
              </NavLink>
            );
          })}
        </nav>
        <MobileMenu items={items} />
      </div>
    </header>
  );
};
